import { useCallback } from 'react';
import { useFormik } from 'formik';
import type { FormikHelpers } from 'formik';
import { usePandaConversation } from './panda-chat-provider';
import { CHAT_SCHEMA } from '../panda-chat.schemas';

export interface ChatComposerValues {
  message: string;
}

interface UsePandaComposerOptions {
  /** Runs right after a message is submitted (e.g. blur the native input). */
  onAfterSubmit?: () => void;
}

export type PandaComposerReturnValue = ReturnType<typeof useFormik<ChatComposerValues>> & {
  canSend: boolean;
};

/**
 * Composer behavior shared by web + mobile, wired to the conversation context:
 * validate, block while a run is in flight, clear the input on send. Called
 * from the input component (not the provider) so formik's per-keystroke state
 * doesn't re-render the whole chat; the input widget itself stays per-platform.
 */
export function usePandaComposer({
  onAfterSubmit,
}: UsePandaComposerOptions = {}): PandaComposerReturnValue {
  const { loading, onMessageSend } = usePandaConversation();

  const onSubmit = useCallback(
    (values: ChatComposerValues, { resetForm }: FormikHelpers<ChatComposerValues>) => {
      if (loading) return;
      resetForm();
      onMessageSend(values);
      onAfterSubmit?.();
    },
    [loading, onMessageSend, onAfterSubmit],
  );

  const formik = useFormik<ChatComposerValues>({
    validationSchema: CHAT_SCHEMA,
    initialValues: { message: '' },
    // Without this, formik reports isValid=true until the first change, which
    // would enable Send on the empty input.
    validateOnMount: true,
    onSubmit,
  });

  return { ...formik, canSend: formik.isValid && !loading };
}
